import { computed, Injectable, signal } from '@angular/core';
import {
  AppNotification,
  NotificationFilter,
  NotificationType,
} from './notification.model';

// ---------------------------------------------------------------------------
// Deterministic seeding helpers
// ---------------------------------------------------------------------------

function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function seedPick<T>(key: string, options: readonly T[]): T {
  return options[djb2(key) % options.length];
}

function isoAgo(daysAgo: number, hoursAgo = 0): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(d.getHours() - hoursAgo);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Demo notification templates
// ---------------------------------------------------------------------------

function buildDemoNotifications(): AppNotification[] {
  return [
    {
      id: 'notif-001',
      type: 'actionRequired',
      title: 'Action Required: MPAP Enrollment Deadline',
      body: 'Pamela Brannan\'s MPAP enrollment window closes in 3 days. Review and submit the application to avoid a gap in coverage.',
      createdAt: isoAgo(0, 2),
      read: false,
      source: 'system',
      clientId: 'session-pamela-001',
      clientName: 'Pamela Brannan',
      target: { route: '/brightai', tab: 'actions', clientId: 'session-pamela-001', clientName: 'Pamela Brannan' },
      cta: { label: 'View Actions', target: { route: '/brightai', tab: 'actions', clientId: 'session-pamela-001', clientName: 'Pamela Brannan' } },
      dismissible: false,
      tags: ['MPAP', 'Deadline'],
    },
    {
      id: 'notif-002',
      type: 'warning',
      title: 'High Credit Utilization Detected',
      body: 'Marcus Johnson\'s credit card utilization has reached 87%. This may negatively impact their credit score.',
      createdAt: isoAgo(0, 5),
      read: false,
      source: 'aiAgent',
      clientId: 'session-marcus-002',
      clientName: 'Marcus Johnson',
      target: { route: '/brightai', tab: 'accounts', clientId: 'session-marcus-002', clientName: 'Marcus Johnson' },
      cta: { label: 'View Accounts', target: { route: '/brightai', tab: 'accounts', clientId: 'session-marcus-002', clientName: 'Marcus Johnson' } },
      dismissible: true,
      tags: ['Credit', 'Utilization'],
    },
    {
      id: 'notif-003',
      type: 'success',
      title: 'Opportunity Committed: Debt Consolidation',
      body: 'The debt consolidation opportunity for Pamela Brannan has been committed. Next step: schedule a follow-up call.',
      createdAt: isoAgo(0, 8),
      read: false,
      source: 'fa',
      clientId: 'session-pamela-001',
      clientName: 'Pamela Brannan',
      target: { route: '/brightai', tab: 'journey', clientId: 'session-pamela-001', clientName: 'Pamela Brannan' },
      cta: { label: 'View Journey', target: { route: '/brightai', tab: 'journey', clientId: 'session-pamela-001', clientName: 'Pamela Brannan' } },
      dismissible: true,
      tags: ['Opportunity', 'Debt'],
    },
    {
      id: 'notif-004',
      type: 'error',
      title: 'Escalation: Client Distress Signal',
      body: 'AI Agent flagged a distress signal during the last chat session with Rosa Martinez. Immediate FA review recommended.',
      createdAt: isoAgo(0, 10),
      read: false,
      source: 'aiAgent',
      clientId: 'session-rosa-003',
      clientName: 'Rosa Martinez',
      target: { route: '/brightai', tab: 'client-chat', clientId: 'session-rosa-003', clientName: 'Rosa Martinez' },
      cta: { label: 'Open Chat', target: { route: '/brightai', tab: 'client-chat', clientId: 'session-rosa-003', clientName: 'Rosa Martinez' } },
      dismissible: false,
      tags: ['Escalation', 'Distress'],
    },
    {
      id: 'notif-005',
      type: 'info',
      title: 'New Equifax Report Available',
      body: 'A new Equifax credit report has been uploaded for Pamela Brannan. Review the updated credit profile.',
      createdAt: isoAgo(1, 0),
      read: false,
      source: 'system',
      clientId: 'session-pamela-001',
      clientName: 'Pamela Brannan',
      target: { route: '/brightai', tab: 'documents', clientId: 'session-pamela-001', clientName: 'Pamela Brannan' },
      cta: { label: 'View Documents', target: { route: '/brightai', tab: 'documents', clientId: 'session-pamela-001', clientName: 'Pamela Brannan' } },
      dismissible: true,
      tags: ['Equifax', 'Credit Report'],
    },
    {
      id: 'notif-006',
      type: 'actionRequired',
      title: 'Action Required: Missing Income Verification',
      body: 'Marcus Johnson\'s Helping Hands application is on hold pending income verification documents.',
      createdAt: isoAgo(1, 4),
      read: false,
      source: 'system',
      clientId: 'session-marcus-002',
      clientName: 'Marcus Johnson',
      target: { route: '/brightai', tab: 'actions', clientId: 'session-marcus-002', clientName: 'Marcus Johnson' },
      cta: { label: 'View Actions', target: { route: '/brightai', tab: 'actions', clientId: 'session-marcus-002', clientName: 'Marcus Johnson' } },
      dismissible: false,
      tags: ['Helping Hands', 'Documents'],
    },
    {
      id: 'notif-007',
      type: 'warning',
      title: 'Stale Account Data: 90+ Days',
      body: 'Several accounts for Rosa Martinez have not been updated in over 90 days. Data may be inaccurate.',
      createdAt: isoAgo(2, 0),
      read: true,
      source: 'system',
      clientId: 'session-rosa-003',
      clientName: 'Rosa Martinez',
      target: { route: '/brightai', tab: 'accounts', clientId: 'session-rosa-003', clientName: 'Rosa Martinez' },
      cta: { label: 'View Accounts', target: { route: '/brightai', tab: 'accounts', clientId: 'session-rosa-003', clientName: 'Rosa Martinez' } },
      dismissible: true,
      tags: ['Accounts', 'Stale Data'],
    },
    {
      id: 'notif-008',
      type: 'success',
      title: 'Credit Score Improved: +42 Points',
      body: 'Pamela Brannan\'s credit score increased by 42 points this month, now at 672. Great progress on debt reduction.',
      createdAt: isoAgo(3, 0),
      read: true,
      source: 'system',
      clientId: 'session-pamela-001',
      clientName: 'Pamela Brannan',
      target: { route: '/brightai', tab: 'finances', clientId: 'session-pamela-001', clientName: 'Pamela Brannan' },
      cta: { label: 'View Finances', target: { route: '/brightai', tab: 'finances', clientId: 'session-pamela-001', clientName: 'Pamela Brannan' } },
      dismissible: true,
      tags: ['Credit Score', 'Milestone'],
    },
    {
      id: 'notif-009',
      type: 'info',
      title: 'AI Agent Completed Autonomous Action',
      body: 'BrightAI autonomously drafted a benefits inquiry letter for Rosa Martinez and is awaiting FA approval.',
      createdAt: isoAgo(3, 6),
      read: true,
      source: 'aiAgent',
      clientId: 'session-rosa-003',
      clientName: 'Rosa Martinez',
      target: { route: '/brightai', tab: 'actions', clientId: 'session-rosa-003', clientName: 'Rosa Martinez' },
      cta: { label: 'Review Action', target: { route: '/brightai', tab: 'actions', clientId: 'session-rosa-003', clientName: 'Rosa Martinez' } },
      dismissible: true,
      tags: ['AI Agent', 'Benefits'],
    },
    {
      id: 'notif-010',
      type: 'warning',
      title: 'High APR Debt Identified',
      body: 'Marcus Johnson has a credit card with 28.9% APR carrying a $4,200 balance. Consider debt restructuring.',
      createdAt: isoAgo(4, 0),
      read: true,
      source: 'aiAgent',
      clientId: 'session-marcus-002',
      clientName: 'Marcus Johnson',
      target: { route: '/brightai', tab: 'accounts', clientId: 'session-marcus-002', clientName: 'Marcus Johnson' },
      cta: { label: 'View Accounts', target: { route: '/brightai', tab: 'accounts', clientId: 'session-marcus-002', clientName: 'Marcus Johnson' } },
      dismissible: true,
      tags: ['APR', 'Debt'],
    },
    {
      id: 'notif-011',
      type: 'info',
      title: 'Client Profile Updated',
      body: 'Pamela Brannan updated her contact information and employment status. Review the updated profile.',
      createdAt: isoAgo(5, 0),
      read: true,
      source: 'client',
      clientId: 'session-pamela-001',
      clientName: 'Pamela Brannan',
      target: { route: '/brightai', tab: 'profile', clientId: 'session-pamela-001', clientName: 'Pamela Brannan' },
      dismissible: true,
      tags: ['Profile'],
    },
    {
      id: 'notif-012',
      type: 'success',
      title: 'Action Completed: Benefits Enrollment',
      body: 'The Helping Hands benefits enrollment action for Rosa Martinez has been marked complete.',
      createdAt: isoAgo(6, 0),
      read: true,
      source: 'fa',
      clientId: 'session-rosa-003',
      clientName: 'Rosa Martinez',
      target: { route: '/brightai', tab: 'journey', clientId: 'session-rosa-003', clientName: 'Rosa Martinez' },
      dismissible: true,
      tags: ['Action', 'Benefits'],
    },
    {
      id: 'notif-013',
      type: 'error',
      title: 'Document Upload Failed',
      body: 'The W-2 form upload for Marcus Johnson failed due to a file format error. Please re-upload.',
      createdAt: isoAgo(7, 0),
      read: true,
      source: 'system',
      clientId: 'session-marcus-002',
      clientName: 'Marcus Johnson',
      target: { route: '/brightai', tab: 'documents', clientId: 'session-marcus-002', clientName: 'Marcus Johnson' },
      cta: { label: 'View Documents', target: { route: '/brightai', tab: 'documents', clientId: 'session-marcus-002', clientName: 'Marcus Johnson' } },
      dismissible: true,
      tags: ['Documents', 'Error'],
    },
  ];
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class NotificationsStoreService {
  private readonly _items = signal<AppNotification[]>(buildDemoNotifications());
  private readonly _filter = signal<NotificationFilter>('all');

  readonly items = this._items.asReadonly();
  readonly activeFilter = this._filter.asReadonly();

  readonly unreadCount = computed(
    () => this._items().filter(n => !n.read).length
  );

  readonly filteredItems = computed((): AppNotification[] => {
    const filter = this._filter();
    const items = this._items();
    switch (filter) {
      case 'unread':
        return items.filter(n => !n.read);
      case 'actionRequired':
        return items.filter(n => n.type === 'actionRequired');
      case 'warnings':
        return items.filter(n => n.type === 'warning' || n.type === 'error');
      default:
        return items;
    }
  });

  setFilter(filter: NotificationFilter): void {
    this._filter.set(filter);
  }

  markRead(id: string): void {
    this._items.update(items =>
      items.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }

  markAllRead(): void {
    this._items.update(items => items.map(n => ({ ...n, read: true })));
  }

  dismiss(id: string): void {
    this._items.update(items => items.filter(n => n.id !== id));
  }

  typeIcon(type: NotificationType): string {
    const icons: Record<NotificationType, string> = {
      info:           'pi-info-circle',
      warning:        'pi-exclamation-triangle',
      error:          'pi-times-circle',
      success:        'pi-check-circle',
      actionRequired: 'pi-bolt',
    };
    return icons[type];
  }
}
